import { scrollMainContentIntoView } from '../utils/scrollUtils';

// messageType should be one of:
// success|info|warning|danger
// dismissAfter should be a timeout in milliseconds (1000 == 1 second)
export const addMessage = (message, messageType = 'info', dismissAfter = false, index = 0) => {
  return (dispatch) => {
    const id = new Date().getTime();
    dispatch({
      type: 'ADD_MESSAGE',
      index,
      item: {
        id,
        message,
        messageType,
      },
    });
    if (dismissAfter) {
      setTimeout(() => {
        dispatch({
          type: 'REMOVE_MESSAGE',
          id,
        });
      }, dismissAfter);
    }
  };
};

// Adds message and sets it as rendered. E.g. The message will be removed on the next page
export const addMessageAndSetRendered = (message, messageType = 'info', dismissAfter = false, index = 0) => {
  return (dispatch) => {
    const id = new Date().getTime();
    const renderedUrl = window.location.href;
    dispatch({
      type: 'ADD_MESSAGE',
      index,
      item: {
        id,
        message,
        renderedUrl,
        messageType,
      },
    });
    if (dismissAfter) {
      setTimeout(() => {
        dispatch({
          type: 'REMOVE_MESSAGE',
          id,
        });
      }, dismissAfter);
    }
  };
};

// Sets the message as being rendered. E.g. The message will be removed on the next page
export const setMessageRendered = (id) => {
  return (dispatch) => {
    const renderedUrl = window.location.href;
    dispatch({
      type: 'SET_MESSAGE_RENDERED',
      id,
      renderedUrl,
    });
  };
};

// Removes the message with the given id
export const removeMessage = (id) => {
  return {
    type: 'REMOVE_MESSAGE',
    id,
  };
};

export const clearMessages = () => {
  return {
    type: 'CLEAR_MESSAGES',
  };
};

// Handle an API error and feed it back to the user.
// Accepts the argument sent to error promise blocks by Superagent.
// For instances where an error meeds to survive across a render refresh (eg. redirecting to
// the login screen immediately after handling an API error when validating a token)
// we can send in a flag to indicate the error shouldn't be set as rendered immediately.
export const handleApiError = (err, messageType, setAsRendered = true) => {
  return (dispatch) => {
    let message = null;
    let sessionExpired = false;

    // Check for 502 Bad Gateway (proxy/connection errors)
    if (message === null) {
      if (typeof err.response !== 'undefined' && err.response.status === 502) {
        message = 'We are currently experiencing difficulties connecting to the Odoo API. Please try again in a few minutes.';
      }
    }

    // Check for AJAX timeout
    if (message === null) {
      if (typeof err.timeout !== 'undefined' && err.timeout) {
        message = 'Something has gone wrong at our end.';
      }
    }

    // Error with recaptcha
    if (message === null) {
      if (typeof err.response !== 'undefined' && err.response.status === 403 && err.response.statusText !== null) {
        message = err.response.statusText;
      }
    }

    // Try finding a JSON response from Odoo
    if (message === null) {
      if (typeof err.response !== 'undefined' && typeof err.response.text !== 'undefined') {
        try {
          const jsonResponse = JSON.parse(err.response.text);
          message = jsonResponse.message;
        } catch {
          /* noop */
        }
      }
    }

    // Try finding an HTML response from Odoo
    if (message === null) {
      if (typeof err.response !== 'undefined' && typeof err.response.text !== 'undefined') {
        try {
          const parsed = new DOMParser().parseFromString(err.response.text, 'text/html');
          const paragraphs = parsed.body.querySelectorAll('p');
          if (paragraphs.length > 0) {
            message = Array.from(paragraphs, (el) => el.textContent.trim()).join('');
          }
          if (message === 'Login failed') {
            message = 'Login failed. Please check your details and try again.';
          }
          if (message === 'login must be present in parameters') {
            message = 'Email and password are required. Please check your details and try again.';
          }
        } catch {
          /* noop */
        }
      }
    }

    // Try finding an exception message, or a message from the Odoo API
    if (message === null) {
      if (typeof err.message !== 'undefined') {
        // HACK - only use this message if our response was to the form API
        // This ignores error messages from browser exceptions.
        if (!(err instanceof Error)) {
          message = err.message;
        }
      }
    }

    // Try finding the HTTP response message
    if (message === null) {
      if (typeof err.response !== 'undefined' && typeof err.response.statusText !== 'undefined') {
        message = err.response.statusText;
      }
    }

    // Fallback to generic error.
    if (message === null) {
      message = 'Something has gone wrong at our end. Please try again.';
    }

    // It's horrible that we are matching the message text, but odoo devs dont understand http status codes.
    if (message === 'The code does not exist') {
      message =
        'The code does not exist. Please double-check the 6-character code in your email and copy and paste it exactly as shown. You can enter it in upper or lower case — it is not case sensitive. Then try again. If you are still having problems, try logging in again to receive a new code.';
    } else if (message === 'confirmation_code must be present in parameters') {
      message =
        'Please enter the 6-character code from your email. You can use upper or lower case — it is not case sensitive. If you did not receive a code, try logging in again to receive a new one.';
    }

    // Clear messages and display our outgoing error
    dispatch(clearMessages());
    if (setAsRendered) {
      dispatch(addMessageAndSetRendered(message, messageType ? messageType : 'danger'));
    } else {
      dispatch(addMessage(message, messageType ? messageType : 'danger'));
    }

    // Match Api.php. Log the user out in these cases.
    // This should trigger a redirect to the login screen
    if (typeof err.response !== 'undefined' && typeof err.response.status === 'number') {
      const requestUrl = typeof err.response.req !== 'undefined' && typeof err.response.req.url === 'string' ? err.response.req.url : '';
      const body = typeof err.response.text === 'string' ? err.response.text : '';
      const isLogout = requestUrl.indexOf('web_portal/api/v1/logout') !== -1;
      const isOdooServerError = err.response.status >= 500;
      const isLoginRequired = err.response.status === 401 && body.indexOf('<p>Login required to access this resource</p>') !== -1;
      const isTwoFaSessionExpired =
        err.response.status === 401 && body.indexOf('<p>No active 2FA session found. Please log in again.</p>') !== -1;
      if (isLogout || isOdooServerError || isLoginRequired || isTwoFaSessionExpired) {
        sessionExpired = true;
      }
    }

    if (sessionExpired) {
      dispatch({
        type: 'SESSION_EXPIRED',
      });
    }

    scrollMainContentIntoView({ skipIfModalDialogPresent: true });
  };
};
